/**
 * Sidebar map (#653). Not a chat list. Not equal tabs.
 * PAGES is omitted: Prose is not its own room; Draft lives off PlotTrack.
 *
 * Series altitude (#670 / #676 / #683 / #696 / #1143): Talk first
 * (Series Discovery, with Chat Sessions and Create New Session quiet
 * under it), then Timeline (the existing corkboard at
 * /projects/:id/series), then Snapshots (one Series Notes room).
 * No Dossiers item. No Outline. No Story Board. No second Timeline
 * page. No book-level Timeline. Docs is one pile
 * with two doors (#672 / #823): Book Planning Docs on the book map,
 * Series Docs on the Timeline toggle. Docs is not a mode-switch.
 */

import {
  BOOK_CARDS_PATH,
  PLOT_NOTEBOOK_PATH,
  bookNotesCategoryFromSearch,
} from '../../utils/bookCards';
import {
  TIER1_CATEGORIES,
  TIER2_CATEGORIES,
} from '../BookSheet/categories';
import {
  isBookDocsOffersPath,
  isBookNotesPath,
  isProjectDocsOffersPath,
  isProjectNotesPath,
} from '../../utils/seriesNotes';
import { isSeriesOffersPath } from '../../utils/seriesOffers';
import { isSeriesSnapshotPath } from '../../utils/seriesSnapshots';
import {
  BOOK_NOTES,
  DOCS,
  DOCS_NAV_TIP,
  DOCS_UPLOAD_TIP,
  FRAMEWORK_BUILDER_NAV_TIP,
  NOTES_NAV_TIP,
  PATH_AUDIT_TIP,
  PLOT_WORKSHOP,
  SCENE_WRITING_NAV_TIP,
  STYLE_NOTES,
  STYLE_NOTES_NAV_TIP,
  STORY_RULES_NAV_TIP,
  TASKS,
} from '../../utils/writerCopy';
import { PLOT_READY_TIP } from '../../utils/plotReadyCopy';

/**
 * Uncollapsed book left-nav hover tips (#957 / #963). Joel locked 2026-09-04.
 * Descriptive tips, not label repeats. Series left-nav is out.
 * Live Book Notes door gets the Notes tip. Style Notes, Story Rules,
 * and Upload leftover tips are #963.
 */
export const BOOK_NAV_HOVER_TIPS = {
  discovery: "Your initial chat session, where you'll work through your story with your AI assistant",
  sessions: 'Browse and reopen past chats for this book',
  create: 'Start an open ended Explore chat, or a specific Focus chat',
  cards: NOTES_NAV_TIP,
  notes: DOCS_NAV_TIP,
  plottrack: 'The scene grid outline for this book',
  'scene-writing': SCENE_WRITING_NAV_TIP,
  'framework-builder': FRAMEWORK_BUILDER_NAV_TIP,
  'plot-notebook': PLOT_READY_TIP,
  path: PATH_AUDIT_TIP,
  'style-cards': STYLE_NOTES_NAV_TIP,
  'story-rules': STORY_RULES_NAV_TIP,
  upload: DOCS_UPLOAD_TIP,
};

/** Book Planning Docs door (#823 / #1025). Same pile as Series Docs. */
export const NOTES_ROOM = {
  id: 'notes',
  label: DOCS,
  kind: 'notes',
  icon: 'notes',
  needsProject: true,
  hoverTip: BOOK_NAV_HOVER_TIPS.notes,
};

const BOOK_NOTES_NAV_CATEGORIES = [...TIER1_CATEGORIES, ...TIER2_CATEGORIES];

function sameBookNoteCategory(a, b) {
  return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
}

/**
 * Book Notes room vs one nested category. The pile (`/bibles` with no
 * known category) lights Notes. A category query lights that link.
 */
export function isBookNotesRouteActive(item, pathname, search) {
  if (!(pathname || '').startsWith(BOOK_CARDS_PATH)) return false;
  const selected = bookNotesCategoryFromSearch(search);
  const known = BOOK_NOTES_NAV_CATEGORIES.some((category) => sameBookNoteCategory(category, selected));
  if (item?.bookNotesCategory) {
    return known && sameBookNoteCategory(selected, item.bookNotesCategory);
  }
  if (item?.id === 'cards') return !known;
  return false;
}

const CHAT_SESSIONS_ITEM = {
  id: 'sessions',
  label: 'Chat Sessions',
  kind: 'route',
  path: '/sessions',
  icon: 'sessions',
  quiet: true,
  hoverTip: BOOK_NAV_HOVER_TIPS.sessions,
};

/**
 * Wave 3A journey rail. Dual-line items use `plain` (ordinary words)
 * plus `label` (product name). Suggested path — not a wizard.
 * Create New Session is a quiet child of Explore, not hero chrome.
 */
/** Rooms the book menu keeps reachable until their own slices absorb them. */
export const BOOK_MENU_REACH = [
  {
    id: 'story-rules',
    label: 'Story Rules',
    kind: 'route',
    path: '/story-rules',
    icon: 'research',
    hoverTip: BOOK_NAV_HOVER_TIPS['story-rules'],
  },
  {
    id: 'plot-notebook',
    label: PLOT_WORKSHOP,
    kind: 'route',
    path: PLOT_NOTEBOOK_PATH,
    icon: 'notes',
    needsStory: true,
    hoverTip: BOOK_NAV_HOVER_TIPS['plot-notebook'],
  },
  {
    id: 'framework-builder',
    label: 'Framework Builder',
    kind: 'route',
    path: '/frameworks',
    icon: 'board',
    needsStory: true,
    hoverTip: BOOK_NAV_HOVER_TIPS['framework-builder'],
  },
];

/**
 * Book sidebar: the book-name menu plus the path and book stops.
 * Idea, Outline, Write, then Notes, Style Notes, and Docs.
 * Category rows stay on the Notes page.
 */
export const SIDEBAR_MAP_GROUPS = [
  {
    id: 'journey',
    label: 'Your path',
    items: [
      {
        id: 'discovery',
        label: 'Idea',
        step: '1',
        kind: 'discovery',
        icon: 'spark',
        hoverTip: BOOK_NAV_HOVER_TIPS.discovery,
      },
      {
        id: 'plottrack',
        label: 'Outline',
        step: '2',
        kind: 'route',
        path: '/plottrack',
        icon: 'board',
        hoverTip: BOOK_NAV_HOVER_TIPS.plottrack,
      },
      {
        id: 'scene-writing',
        label: 'Write',
        step: '3',
        kind: 'route',
        path: '/scene-writing',
        icon: 'novel',
        hoverTip: BOOK_NAV_HOVER_TIPS['scene-writing'],
      },
    ],
  },
  {
    id: 'your-book',
    label: 'Your book',
    items: [
      {
        id: 'cards',
        label: 'Notes',
        kind: 'route',
        path: BOOK_CARDS_PATH,
        icon: 'bible',
        hoverTip: BOOK_NAV_HOVER_TIPS.cards,
      },
      {
        id: 'style-cards',
        label: STYLE_NOTES,
        kind: 'route',
        path: '/style-cards',
        icon: 'compendium',
        needsStory: true,
        hoverTip: BOOK_NAV_HOVER_TIPS['style-cards'],
      },
      NOTES_ROOM,
    ],
  },
];

/**
 * The old tools bar and breadcrumb stay off while a book is open,
 * and off the book rooms even before the story record has loaded.
 * The projects list keeps them when a story is still current (#1840).
 * A current story used to hide the bar on that list too.
 * Book rooms use the one-at-a-time right slot (showRightPanelSlot)
 * instead of putting Chat beside Compendium (#2074).
 */
export function showLegacyAppBar(pathname, storyId) {
  const path = pathname || '';
  if (path === '/' || path === '/projects' || path === '/projects/') return true;
  if (storyId) return false;
  if (path.startsWith('/plottrack')) return false;
  if (path.startsWith('/scene-writing')) return false;
  if (path.startsWith('/chat')) return false;
  if (path.startsWith('/bibles')) return false;
  if (path.startsWith('/sessions')) return false;
  if (path.startsWith('/frameworks')) return false;
  if (path.startsWith('/style-cards')) return false;
  if (path.startsWith('/story-rules')) return false;
  if (path.startsWith('/search')) return false;
  if (path.startsWith('/graph')) return false;
  return true;
}

/**
 * One right-hand slot (#2074 / #2075).
 * Every full-page chat lives under /chat: Discovery, Explore, Focus,
 * and the other main chats. Outline, Write, Notes, and Docs keep the
 * room chat as one choice in the same slot. Story progress is one of
 * those choices on all of them.
 * Docs matches the room chat: any path that includes `/notes`.
 * The projects list keeps the old top bar and does not get this slot.
 */
export function showRightPanelSlot(pathname) {
  const path = pathname || '';
  if (path === '/' || path === '/projects' || path === '/projects/') return false;
  if (path.startsWith('/plottrack')) return true;
  if (path.startsWith('/scene-writing')) return true;
  if (path.startsWith('/bibles')) return true;
  if (path.startsWith('/chat')) return true;
  if (path.includes('/notes')) return true;
  return false;
}

/**
 * Idea, Outline, Scene writing, Notes, and Docs open Names, Research,
 * Compendium, and Story progress from the title row.
 */
export function showBookToolsRow(pathname) {
  const path = pathname || '';
  if (path.startsWith('/plottrack')) return true;
  if (path.startsWith('/scene-writing')) return true;
  if (path.startsWith('/chat')) return true;
  if (path.startsWith('/bibles')) return true;
  if (path.includes('/notes')) return true;
  return false;
}

export const BOOK_TOOL_IDS = new Set([
  'cards',
  'style-cards',
  'story-rules',
  'plot-notebook',
  'framework-builder',
  'notes',
]);

/**
 * Series Talk (#1143). Same child shape as Explore on the book map:
 * Chat Sessions, then Create New Session, both quiet. Create New Session
 * is not hero chrome.
 */
export const SERIES_TALK_GROUP = {
  id: 'talk',
  label: 'Chat',
  items: [
    {
      id: 'discovery',
      label: 'Series Discovery',
      kind: 'discovery',
      icon: 'spark',
      children: [
        {
          id: 'sessions',
          label: 'Chat Sessions',
          kind: 'route',
          path: '/sessions',
          icon: 'sessions',
          quiet: true,
        },
        {
          id: 'create',
          label: 'Create New Session',
          kind: 'create',
          icon: 'new',
          quiet: true,
        },
      ],
    },
  ],
};

export const SERIES_TIMELINE_GROUP = {
  id: 'timeline',
  label: 'Timeline',
  items: [
    {
      id: 'timeline',
      label: 'Timeline',
      kind: 'timeline',
      icon: 'timeline',
      needsProject: true,
    },
  ],
};

export const SERIES_SNAPSHOTS_GROUP = {
  id: 'snapshots',
  label: 'Snapshots',
  items: [
    { id: 'series-cards', label: 'Series Notes', kind: 'series-cards', icon: 'bible' },
  ],
};

export const SERIES_SIDEBAR_MAP_GROUPS = [
  SERIES_TALK_GROUP,
  SERIES_TIMELINE_GROUP,
  SERIES_SNAPSHOTS_GROUP,
];

export const SIDEBAR_SIDE_DOOR = {
  id: 'upload',
  label: 'Upload',
  kind: 'action',
  icon: 'upload',
  needsProject: true,
};

/** Book-map Upload door (#963). Series keeps SIDEBAR_SIDE_DOOR with no tip. */
export const BOOK_SIDE_DOOR = {
  ...SIDEBAR_SIDE_DOOR,
  hoverTip: BOOK_NAV_HOVER_TIPS.upload,
};

/** Mobile drawer only (#2754). Opens Compendium on the Saved ideas tab. */
export const SAVED_IDEAS_DRAWER_ITEM = {
  id: 'saved-ideas',
  label: 'Saved ideas',
  kind: 'saved-ideas',
  icon: 'compendium',
  needsProject: true,
};

function flattenGroupItems(groups) {
  return groups.flatMap((group) => (
    group.items.flatMap((item) => [item, ...(item.children || [])])
  ));
}

export function flattenMapItems() {
  return flattenGroupItems(SIDEBAR_MAP_GROUPS);
}

export function flattenSeriesMapItems() {
  return [
    ...flattenGroupItems(SERIES_SIDEBAR_MAP_GROUPS),
    SIDEBAR_SIDE_DOOR,
  ];
}

export function isDiscoveryPath(pathname, currentSessionId, discoveryIds) {
  const path = pathname || '';
  if (path.startsWith('/projects/') && /\/discovery\/?$/.test(path)) return true;
  // Leftover Discovery sitting must not light Explore on another room (#1127).
  if (!path.startsWith('/chat/')) return false;
  if (!currentSessionId) return false;
  return (discoveryIds || []).includes(currentSessionId);
}

export function isSeriesRoomPath(pathname) {
  return /^\/projects\/[^/]+\/series\/?$/.test(pathname || '');
}

export function isSeriesDiscoveryPath(pathname) {
  return /^\/projects\/[^/]+\/discovery\/?$/.test(pathname || '');
}

/**
 * Series altitude: the series Talk + Timeline + Snapshots map, not the book map.
 * True on the Series room, Series Notes (and leftover dossier URLs),
 * Make notes from chats and docs, project-level Discovery, a project-scoped sitting, Chat
 * Sessions with no book selected, a /chat sitting with no book selected
 * (Series Discovery first-open — do not wait for currentSession to load),
 * or Series Notes when no book is selected. Book Planning Docs
 * stays on the book map even before currentStory hydrates (#823).
 */
export function isSeriesAltitude({
  isSeries,
  pathname,
  currentStory,
  currentSession,
} = {}) {
  if (!isSeries) return false;
  if (isBookNotesPath(pathname) || isBookDocsOffersPath(pathname)) return false;
  if (isSeriesRoomPath(pathname) || isSeriesDiscoveryPath(pathname)) return true;
  if (isSeriesOffersPath(pathname)) return true;
  // #1417: Series Notes opened from inside a book keeps the book map (the writer reads the
  // series notes as reference and goes back to the book). From the series map, the door
  // clears the book first, so it stays series altitude.
  if (isSeriesSnapshotPath(pathname)) return !currentStory?.id;
  if ((isProjectNotesPath(pathname) || isProjectDocsOffersPath(pathname)) && !currentStory) return true;
  // A leftover project session (no story_id) must not steal the book map
  // after a book is open. Create New Session stays in that book (#1226).
  if (currentSession && !currentSession.story_id && !currentStory?.id) return true;
  if (!currentStory && (pathname || '').startsWith('/sessions')) return true;
  // Empty series lands on /chat/{series-discovery-id}. currentSession is
  // still null on first paint; waiting for it disabled Create New Session.
  if (!currentStory && (pathname || '').startsWith('/chat/')) return true;
  return false;
}

/**
 * Props for generic Create New Session (#1226).
 * An open book — or the book map, once the sitting has a story id —
 * always launches inside that book. Series altitude with no book does
 * not launch. This flow never starts a new story.
 */
export function createSessionLaunch({
  atSeriesAltitude = false,
  currentProject = null,
  currentStory = null,
  currentSession = null,
} = {}) {
  const fromStory = currentStory?.id || null;
  // Book map can show before currentStory hydrates. A series project
  // with no currentStory.id used to null storyId and look like series
  // altitude. The sitting's story id is the open book.
  const fromSession = !atSeriesAltitude ? (currentSession?.story_id || null) : null;
  const bookId = fromStory || fromSession;
  if (!bookId) return null;
  if (atSeriesAltitude && !fromStory) return null;
  return {
    seriesAltitude: false,
    storyId: bookId,
    projectId: currentProject?.id || null,
  };
}

export { isBookNotesPath, isProjectNotesPath, isSeriesSnapshotPath };

export function journeyStageFromPath(pathname, currentSessionId, discoveryIds) {
  const path = pathname || '';
  if (isDiscoveryPath(path, currentSessionId, discoveryIds)) return 'explore';
  if (path === '/tasks' || path.startsWith('/tasks/') || path === '/path' || path.startsWith('/path/')) {
    return 'tasks';
  }
  if (path === '/dossiers' || path.startsWith('/dossiers/')) return 'plot-workshop';
  if (path === '/plottrack' || path.startsWith('/plottrack/')) return 'plottrack';
  if (path === '/scene-writing' || path.startsWith('/scene-writing/')) return 'scene-writing';
  return null;
}

const JOURNEY_ROOM_LABEL = {
  explore: 'Idea',
  tasks: TASKS,
  'plot-workshop': PLOT_WORKSHOP,
  plottrack: 'Outline',
  'scene-writing': 'Write',
};

/**
 * Session-bar room name. Suggested-path rooms follow the URL, not a
 * leftover Discovery sitting (#1127). Chat Sessions is a child of Explore.
 */
export function breadcrumbRoomFromPath(pathname, currentSessionId, discoveryIds) {
  const path = pathname || '';
  if (isChatSessionsPath(path, currentSessionId, discoveryIds)
    && !isDiscoveryPath(path, currentSessionId, discoveryIds)) {
    return CHAT_SESSIONS_ITEM.label;
  }
  // Run 43: Book Notes showed a leftover Plot Workshop sitting's name.
  if (isBookNotesPath(path)) return BOOK_NOTES;
  const stage = journeyStageFromPath(path, currentSessionId, discoveryIds);
  return JOURNEY_ROOM_LABEL[stage] || null;
}

/** True when the URL itself is a Discovery or chat sitting. */
export function isSessionSittingPath(pathname) {
  const path = pathname || '';
  if (path.startsWith('/projects/') && /\/discovery\/?$/.test(path)) return true;
  return path.startsWith('/chat/');
}

export function isChatSessionsPath(pathname, currentSessionId, discoveryIds) {
  if (pathname.startsWith('/sessions')) return true;
  if (!pathname.startsWith('/chat/')) return false;
  if (!currentSessionId) return true;
  return !discoveryIds.includes(currentSessionId);
}
